'use client';
import {
  BarraProgreso,
  Boton,
  Campo,
  CampoNumero,
  Chip,
  Estadistica,
  HojaInferior,
  Icono,
  IndicadorSync,
  Tarjeta,
  cx
} from "./chunk-3Q2RVIFA.js";

// src/esqueleto.tsx
import { useEffect, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var BASE = "esqueleto bg-stone-200 dark:bg-stone-800";
var ESPERA_MS = 150;
var CONTINUIDAD_MS = 100;
var aLaVista = 0;
var ultimaQueSeFue = Number.NEGATIVE_INFINITY;
function useContinuaOtra() {
  const [continua] = useState(
    () => aLaVista > 0 || typeof performance !== "undefined" && performance.now() - ultimaQueSeFue < CONTINUIDAD_MS
  );
  useEffect(() => {
    let vista = false;
    const ver = () => {
      vista = true;
      aLaVista++;
    };
    const t = continua ? void 0 : setTimeout(ver, ESPERA_MS);
    if (continua) ver();
    return () => {
      clearTimeout(t);
      if (!vista) return;
      aLaVista--;
      ultimaQueSeFue = performance.now();
    };
  }, []);
  return continua;
}
function ZonaCargando({ texto = "Cargando\u2026", inmediata, className, children, ...resto }) {
  const continua = useContinuaOtra();
  return /* @__PURE__ */ jsxs("div", { "aria-busy": "true", className: cx(!inmediata && !continua && "esqueleto-espera", className), ...resto, children: [
    /* @__PURE__ */ jsx("span", { role: "status", className: "sr-only", children: texto }),
    /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: "contents", children })
  ] });
}
function Esqueleto({ className, ...resto }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": "true",
      className: cx(BASE, !/(^|\s)rounded/.test(className ?? "") && "rounded-lg", className),
      ...resto
    }
  );
}
function EsqueletoEnLinea({ ancho = "4em", className }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      className: cx(BASE, "inline-block h-[0.8em] rounded-md align-middle", className),
      style: { width: ancho }
    }
  );
}
var RENGLON = {
  xs: ["h-4", "h-2.5"],
  sm: ["h-5", "h-3"],
  base: ["h-6", "h-3.5"],
  lg: ["h-7", "h-4"],
  xl: ["h-7", "h-4.5"],
  "2xl": ["h-8", "h-5"],
  "3xl": ["h-9", "h-6"]
};
var ANCHOS_RENGLON = ["100%", "92%", "96%", "88%"];
function EsqueletoRenglon({
  tamano = "sm",
  ancho = "100%",
  className
}) {
  const [alto, barra] = RENGLON[tamano];
  return /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: cx("flex items-center", alto, className), children: /* @__PURE__ */ jsx(Esqueleto, { className: cx(barra, "rounded-full"), style: { width: ancho } }) });
}
function EsqueletoTexto({
  renglones = 3,
  tamano = "sm",
  className
}) {
  return /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className, children: Array.from({ length: renglones }, (_, i) => /* @__PURE__ */ jsx(
    EsqueletoRenglon,
    {
      tamano,
      ancho: renglones > 1 && i === renglones - 1 ? "60%" : ANCHOS_RENGLON[i % ANCHOS_RENGLON.length]
    },
    i
  )) });
}
var VARIACION = [1, 0.78, 0.9, 0.68, 0.84, 0.95, 0.74];
function CeldaEsqueleto({ c, fila }) {
  const f = VARIACION[fila % VARIACION.length];
  if (c.forma === "boton") {
    const ancho = c.ancho ?? 2.25;
    return /* @__PURE__ */ jsx("div", { className: cx("flex", c.derecha && "justify-end"), children: /* @__PURE__ */ jsx(Esqueleto, { className: "rounded-full", style: { width: `${ancho}rem`, height: `${Math.min(ancho, 2.25)}rem` } }) });
  }
  if (c.forma === "chip")
    return /* @__PURE__ */ jsx("div", { className: cx("flex h-5 items-center", c.derecha && "justify-end"), children: /* @__PURE__ */ jsx(Esqueleto, { className: "h-5 rounded-full", style: { width: `${(c.ancho ?? 5) * (0.85 + f * 0.15)}rem` } }) });
  return /* @__PURE__ */ jsxs("div", { className: cx("flex flex-col", c.derecha && "items-end"), children: [
    Array.from({ length: c.renglones ?? 1 }, (_, i) => /* @__PURE__ */ jsx(
      EsqueletoRenglon,
      {
        ancho: `${(c.ancho ?? 6) * (i ? 0.7 * VARIACION[(fila + 2) % VARIACION.length] : f)}rem`,
        className: cx(c.derecha && "justify-end")
      },
      i
    )),
    c.detalle && /* @__PURE__ */ jsx(
      EsqueletoRenglon,
      {
        tamano: "xs",
        ancho: `${(c.ancho ?? 6) * 0.85 * VARIACION[(fila + 3) % VARIACION.length]}rem`,
        className: cx(c.derecha && "justify-end")
      }
    )
  ] });
}
function EsqueletoTabla({
  filas = 6,
  columnas = 4,
  encabezado = true,
  className
}) {
  const cols = typeof columnas === "number" ? Array.from({ length: columnas }, () => ({})) : columnas;
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": "true",
      className: cx(
        "overflow-hidden rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900",
        className
      ),
      children: /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-sm", children: [
        encabezado && /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { children: cols.map((c, i) => /* @__PURE__ */ jsx(
          "th",
          {
            className: cx(
              "whitespace-nowrap border-b border-stone-200 bg-stone-50 px-3 py-2.5 text-xs font-medium uppercase tracking-wide text-stone-500 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-400",
              c.derecha ? "text-right" : "text-left",
              c.forma === "boton" && "w-10"
            ),
            children: c.titulo ?? (c.forma === "boton" ? null : /* @__PURE__ */ jsx(EsqueletoRenglon, { tamano: "xs", ancho: "3.5rem", className: cx(c.derecha && "justify-end") }))
          },
          i
        )) }) }),
        /* @__PURE__ */ jsx("tbody", { children: Array.from({ length: filas }, (_, f) => /* @__PURE__ */ jsx("tr", { children: cols.map((c, i) => /* @__PURE__ */ jsx("td", { className: "border-b border-stone-100 px-3 py-2.5 align-middle dark:border-stone-800/70", children: /* @__PURE__ */ jsx(CeldaEsqueleto, { c, fila: f }) }, i)) }, f)) })
      ] })
    }
  );
}
function EsqueletoTarjeta({
  renglones = 2,
  avatar,
  chip,
  compacta,
  className,
  style,
  children
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": "true",
      style,
      className: cx(
        "border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900",
        compacta ? "rounded-2xl p-3" : "rounded-3xl p-4",
        className
      ),
      children: children ?? /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
        avatar && /* @__PURE__ */ jsx(Esqueleto, { className: "h-10 w-10 shrink-0 rounded-full" }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
            /* @__PURE__ */ jsx(EsqueletoRenglon, { tamano: "base", ancho: "55%", className: "flex-1" }),
            chip && /* @__PURE__ */ jsx(Esqueleto, { className: "h-5 w-20 shrink-0 rounded-full" })
          ] }),
          renglones > 0 && /* @__PURE__ */ jsx(EsqueletoTexto, { renglones, tamano: "xs", className: "mt-1 w-4/5" })
        ] })
      ] })
    }
  );
}
function EsqueletoLista({
  filas = 4,
  className,
  ...tarjeta
}) {
  return /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: cx("space-y-2", className), children: Array.from({ length: filas }, (_, i) => /* @__PURE__ */ jsx(EsqueletoTarjeta, { ...tarjeta }, i)) });
}

// src/toast.tsx
import { createContext, useCallback, useContext, useMemo, useRef, useState as useState2 } from "react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Contexto = createContext(null);
function ProveedorToast({ children }) {
  const [toasts, setToasts] = useState2([]);
  const siguiente = useRef(1);
  const mostrar = useCallback((mensaje, opciones = {}) => {
    const id = siguiente.current++;
    setToasts((t) => [...t.slice(-2), { id, mensaje, tipo: opciones.tipo ?? "ok", icono: opciones.icono }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opciones.duracionMs ?? 3e3);
  }, []);
  const valor = useMemo(() => ({ mostrar }), [mostrar]);
  return /* @__PURE__ */ jsxs2(Contexto.Provider, { value: valor, children: [
    children,
    /* @__PURE__ */ jsx2(
      "div",
      {
        className: "pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4",
        "aria-live": "polite",
        children: toasts.map((t) => /* @__PURE__ */ jsxs2(
          "div",
          {
            className: cx(
              "animar-subir flex max-w-md items-center gap-2 rounded-2xl px-4 py-3 text-sm shadow-lg",
              t.tipo === "error" ? "bg-rose-600 text-white" : "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
            ),
            children: [
              /* @__PURE__ */ jsx2(
                Icono,
                {
                  nombre: t.icono ?? (t.tipo === "error" ? "error" : t.tipo === "info" ? "info" : "check_circle"),
                  tamano: 20
                }
              ),
              /* @__PURE__ */ jsx2("span", { children: t.mensaje })
            ]
          },
          t.id
        ))
      }
    )
  ] });
}
function useToast() {
  const c = useContext(Contexto);
  if (!c) throw new Error("useToast necesita <ProveedorToast>");
  return c;
}

// src/google.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function LogoGoogle({ tamano = 20 }) {
  return /* @__PURE__ */ jsxs3("svg", { "aria-hidden": "true", width: tamano, height: tamano, viewBox: "0 0 48 48", className: "shrink-0", children: [
    /* @__PURE__ */ jsx3(
      "path",
      {
        fill: "#EA4335",
        d: "M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      }
    ),
    /* @__PURE__ */ jsx3(
      "path",
      {
        fill: "#4285F4",
        d: "M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      }
    ),
    /* @__PURE__ */ jsx3(
      "path",
      {
        fill: "#FBBC05",
        d: "M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      }
    ),
    /* @__PURE__ */ jsx3(
      "path",
      {
        fill: "#34A853",
        d: "M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      }
    )
  ] });
}
function BotonGoogle({
  cargando,
  texto = "Continuar con Google",
  tamano = "md",
  className,
  disabled,
  type = "button",
  ...resto
}) {
  return /* @__PURE__ */ jsxs3(
    "button",
    {
      type,
      ...resto,
      disabled: disabled || cargando,
      "aria-busy": cargando || void 0,
      className: cx(
        "flex w-full items-center justify-center gap-3 rounded-full border px-5 transition-colors select-none",
        tamano === "lg" ? "h-14 text-base" : "h-12 text-[15px]",
        "font-[Roboto,Arial,sans-serif] font-medium tracking-[0.25px]",
        "border-[#747775] bg-[#ffffff] text-[#1f1f1f] hover:bg-[#f8faff] active:bg-[#eef2fb]",
        "dark:border-[#8e918f] dark:bg-[#131314] dark:text-[#e3e3e3] dark:hover:bg-[#1f2023]",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
        "disabled:cursor-wait disabled:opacity-70",
        className
      ),
      children: [
        cargando ? /* @__PURE__ */ jsx3(Icono, { nombre: "progress_activity", tamano: 20, className: "animate-spin text-[#4285f4]" }) : /* @__PURE__ */ jsx3(LogoGoogle, {}),
        /* @__PURE__ */ jsx3("span", { children: texto })
      ]
    }
  );
}

// src/proveedores.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function LogoApple({ tamano = 20, className }) {
  return /* @__PURE__ */ jsx4(
    "svg",
    {
      "aria-hidden": "true",
      width: tamano,
      height: tamano,
      viewBox: "0 0 24 24",
      fill: "currentColor",
      className: cx("shrink-0", className),
      children: /* @__PURE__ */ jsx4("path", { d: "M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" })
    }
  );
}
var F_FACEBOOK = "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z";
function LogoFacebook({ tamano = 20, blanco }) {
  return /* @__PURE__ */ jsxs4("svg", { "aria-hidden": "true", width: tamano, height: tamano, viewBox: "0 0 24 24", className: "shrink-0", children: [
    blanco ? null : /* @__PURE__ */ jsx4("circle", { cx: "12", cy: "12", r: "11", fill: "#ffffff" }),
    /* @__PURE__ */ jsx4("path", { fill: blanco ? "#ffffff" : "#0866FF", d: F_FACEBOOK })
  ] });
}
function LogoProveedor({ proveedor, tamano = 20 }) {
  if (proveedor === "apple") return /* @__PURE__ */ jsx4(LogoApple, { tamano });
  if (proveedor === "facebook") return /* @__PURE__ */ jsx4(LogoFacebook, { tamano });
  return /* @__PURE__ */ jsx4(LogoGoogle, { tamano });
}
var BASE2 = (tamano) => cx(
  "flex w-full items-center justify-center gap-3 rounded-full border px-5 transition-colors select-none",
  tamano === "lg" ? "h-14 text-base" : "h-12 text-[15px]",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
  "disabled:cursor-wait disabled:opacity-70"
);
function BotonApple({
  cargando,
  texto = "Continuar con Apple",
  tamano = "md",
  className,
  disabled,
  type = "button",
  ...resto
}) {
  return /* @__PURE__ */ jsxs4(
    "button",
    {
      type,
      ...resto,
      disabled: disabled || cargando,
      "aria-busy": cargando || void 0,
      className: cx(
        BASE2(tamano),
        "font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif] font-medium",
        "border-[#000000] bg-[#000000] text-[#ffffff] hover:bg-[#1d1d1f] active:bg-[#333336]",
        "dark:border-[#ffffff] dark:bg-[#ffffff] dark:text-[#000000] dark:hover:bg-[#ececee] dark:active:bg-[#dcdce0]",
        className
      ),
      children: [
        cargando ? /* @__PURE__ */ jsx4(Icono, { nombre: "progress_activity", tamano: 20, className: "animate-spin" }) : /* @__PURE__ */ jsx4(LogoApple, { className: "-mt-0.5" }),
        /* @__PURE__ */ jsx4("span", { children: texto })
      ]
    }
  );
}
function BotonFacebook({
  cargando,
  texto = "Continuar con Facebook",
  tamano = "md",
  className,
  disabled,
  type = "button",
  ...resto
}) {
  return /* @__PURE__ */ jsxs4(
    "button",
    {
      type,
      ...resto,
      disabled: disabled || cargando,
      "aria-busy": cargando || void 0,
      className: cx(
        BASE2(tamano),
        "font-[Helvetica,Arial,sans-serif] font-semibold",
        "border-[#0866ff] bg-[#0866ff] text-[#ffffff] hover:bg-[#0759e0] active:bg-[#064fc7]",
        className
      ),
      children: [
        cargando ? /* @__PURE__ */ jsx4(Icono, { nombre: "progress_activity", tamano: 20, className: "animate-spin" }) : /* @__PURE__ */ jsx4(LogoFacebook, { blanco: true }),
        /* @__PURE__ */ jsx4("span", { children: texto })
      ]
    }
  );
}
function BotonProveedor({ proveedor, ...resto }) {
  if (proveedor === "apple") return /* @__PURE__ */ jsx4(BotonApple, { ...resto });
  if (proveedor === "facebook") return /* @__PURE__ */ jsx4(BotonFacebook, { ...resto });
  return /* @__PURE__ */ jsx4(BotonGoogle, { ...resto });
}

// src/firma.tsx
import { useEffect as useEffect2, useRef as useRef2, useState as useState3 } from "react";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function Firma({
  onCambio,
  etiqueta = "Recuadro para firmar"
}) {
  const ref = useRef2(null);
  const [hay, setHay] = useState3(false);
  useEffect2(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let medido = false;
    const preparar = () => {
      const r = cv.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(r.width * dpr);
      cv.height = Math.round(r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, r.width, r.height);
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#111111";
      medido = true;
    };
    preparar();
    let dibujando = false;
    const pos = (e) => {
      const b = cv.getBoundingClientRect();
      return [e.clientX - b.left, e.clientY - b.top];
    };
    const abajo = (e) => {
      if (!medido) preparar();
      dibujando = true;
      cv.setPointerCapture(e.pointerId);
      ctx.beginPath();
      ctx.moveTo(...pos(e));
    };
    const mover = (e) => {
      if (!dibujando) return;
      ctx.lineTo(...pos(e));
      ctx.stroke();
      setHay(true);
    };
    const arriba = () => {
      if (!dibujando) return;
      dibujando = false;
      exportar();
    };
    cv.addEventListener("pointerdown", abajo);
    cv.addEventListener("pointermove", mover);
    cv.addEventListener("pointerup", arriba);
    cv.addEventListener("pointercancel", arriba);
    return () => {
      cv.removeEventListener("pointerdown", abajo);
      cv.removeEventListener("pointermove", mover);
      cv.removeEventListener("pointerup", arriba);
      cv.removeEventListener("pointercancel", arriba);
    };
  }, []);
  function exportar() {
    const cv = ref.current;
    if (!cv) return;
    const escala = Math.min(1, 600 / cv.width);
    const salida = document.createElement("canvas");
    salida.width = Math.round(cv.width * escala);
    salida.height = Math.round(cv.height * escala);
    salida.getContext("2d").drawImage(cv, 0, 0, salida.width, salida.height);
    salida.toBlob(
      (b) => b && onCambio({ blob: b, ancho: salida.width, alto: salida.height, url: URL.createObjectURL(b) }),
      "image/png"
    );
  }
  function borrar() {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const r = cv.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, r.width, r.height);
    setHay(false);
    onCambio(null);
  }
  return (
    // En tablet o notebook el recuadro no pasa de 512 px de ancho: alcanza para firmar y no queda gigante.
    /* @__PURE__ */ jsxs5("div", { className: "sm:mx-auto sm:max-w-lg", children: [
      /* @__PURE__ */ jsx5(
        "canvas",
        {
          ref,
          className: "firma h-40 w-full touch-none rounded-2xl border border-dashed border-stone-300 bg-white dark:border-stone-600",
          "aria-label": etiqueta
        }
      ),
      /* @__PURE__ */ jsxs5("div", { className: "mt-1 flex items-center justify-between text-xs text-stone-500", children: [
        /* @__PURE__ */ jsx5("span", { children: hay ? "Firma lista" : "Firm\xE1 con el dedo dentro del recuadro" }),
        hay ? /* @__PURE__ */ jsx5(Boton, { variante: "fantasma", tamano: "sm", icono: "ink_eraser", onClick: borrar, children: "Borrar" }) : null
      ] })
    ] })
  );
}
export {
  BarraProgreso,
  Boton,
  BotonApple,
  BotonFacebook,
  BotonGoogle,
  BotonProveedor,
  Campo,
  CampoNumero,
  Chip,
  Esqueleto,
  EsqueletoEnLinea,
  EsqueletoLista,
  EsqueletoRenglon,
  EsqueletoTabla,
  EsqueletoTarjeta,
  EsqueletoTexto,
  Estadistica,
  Firma,
  HojaInferior,
  Icono,
  IndicadorSync,
  LogoApple,
  LogoFacebook,
  LogoGoogle,
  LogoProveedor,
  ProveedorToast,
  Tarjeta,
  ZonaCargando,
  cx,
  useToast
};
//# sourceMappingURL=index.js.map